import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import Grid from '@mui/material/Grid';
import { Stack, Typography, Divider, Button } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InnerLayout from '../../styles/InnerLayout';
import { Subtitle } from '../forms/Title';
import InputTextBox from '../forms/InputTextBox';
import HelpCard from '../forms/HelpCard';
import CampaignDetailsCard from '../forms/CampaignDetailsCard';
import CampaignSummary from '../forms/CampaignSummary';
import { UseStores } from '../../hooks/UseStores';
import {
  ABDM_MIN_ADDRESSES,
  ABDM_POSTCARD_DESIGN_RATE,
} from '../../utils/constants';
import { validateEmail, scrollToSection } from '../../utils/abdmUtils';
import dayjs from 'dayjs';
import { createABDMCampaign } from '../../utils/api';
import ApiErrorModal from '../forms/ApiErrorModal';
import { Link } from '@mui/material';
import palette from '../../styles/palette';
import { FormControlLabel } from '@mui/material';
import GtCheckbox from '../../styles/CustomCheckbox';

const DATE_FORMAT = 'MM/DD/YYYY';

const OrderSection = () => {
  const { audience, mapstore, store } = UseStores();
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const handleTenant = (value) => {
    store.setTenantName(value);
  };
  const handleAccount = (value) => {
    store.setAccountName(value);
  };

  const handleName = (value) => {
    store.setFullName(value);
  };

  const handleEmail = (value) => {
    store.setEmail(value);
  };

  const handleHelpToggle = () => {
    store.handleHelpRequiredToggle();
    if (store.helpRequired) {
      mapstore.setPrice(Number(mapstore.price) + ABDM_POSTCARD_DESIGN_RATE);
    } else {
      mapstore.setPrice(Number(mapstore.price) - ABDM_POSTCARD_DESIGN_RATE);
    }
  };

  const handleGtAccountToggle = () => {
    store.handleGtResellerToggle();
  };

  const onSubmit = () => {
    const selectedDateRange = `${dayjs(
      store.selectedAvailability.date_start,
    ).format(DATE_FORMAT)}-${dayjs(store.selectedAvailability.date_end).format(
      DATE_FORMAT,
    )}`;

    const payload = {
      audience: audience.selectedAudience.value,
      zip_codes: Object.keys(mapstore.selectedZipCodeList).map(Number),
      total_addresses: mapstore.totalAddresses,
      delivery_dates: selectedDateRange.toString(),
      cost_per_unit: parseFloat(mapstore.rate),
      price: parseFloat(mapstore.price),
      tenant_name: store.tenantName,
      account_name: store.accountName,
      user_name: store.name,
      email: store.email,
      help_needed_for_postcard_design: store.helpRequired,
      is_GT_reseller: store.isGtReseller,
    };

    const result = createABDMCampaign(JSON.stringify(payload));

    result
      .then((response) => {
        const { status } = response;
        if (status === 200) {
          setIsSuccess(!isSuccess);
        } else {
          setIsError(!isError);
        }
      })
      .catch(() => {
        setIsError(!isError);
      });
  };

  const styles = {
    orderSectionBox: {
      padding: {
        xs: 'calc(1vh)',
        md: 'calc(3vh)',
      },
    },
    campaignDetailsBox: {
      marginTop: { xs: '0vh', sm: '15px', lg: '-4px' },
    },
    reserveCampaignBox: {
      marginBottom: 'calc(3vh)',
    },
    textMargin: {
      marginTop: { lg: '20px', xs: '14px' },
    },
    inputField: {
      width: { lg: '49%', xs: '100%', sm: '48.4%' },
      marginBottom: { xs: 'calc(3vh)', sm: 'calc(0vh)' },
      marginRight: { sm: '10px' },
      marginTop: '-28px',
    },
    textField: {
      width: { lg: '49%', xs: '100%', sm: '48.4%' },
      marginTop: { xs: '1px', sm: '-28px' },
    },
    inputAccountField: {
      marginTop: '-28px',
      width: '100%',
    },
    checkbox: {
      marginTop: '-24px',
      '&+ .MuiFormControlLabel-label': {
        marginTop: '-24px',
      },
      marginLeft: '-6px',
    },
    divider: {
      marginTop: '18px',
    },
    button: {
      marginTop: '16px',
    },
    inputBox: {
      justifyContent: 'space-between',
    },
  };

  const textStyles = {
    color: palette.gt_text_primary,
    marginTop: '20px',
  };

  const termslinkStyle = {
    color: palette.gt_teal_6,
    textDecoration: 'none',
  };

  useEffect(() => {
    if (
      audience.selectedAudience.label &&
      store.selectedAvailability &&
      mapstore.totalAddresses >= ABDM_MIN_ADDRESSES &&
      Object.keys(mapstore.selectedZipCodeList).length > 0 &&
      validateEmail(store.email)
    ) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [
    audience.selectedAudience.label,
    mapstore.totalAddresses,
    mapstore.selectedZipCodeList,
    store.email,
    store.selectedAvailability,
  ]);

  return (
    <Grid
      container
      rowSpacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}
      columnSpacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}
      sx={styles.orderSectionBox}
      id="order-summary"
    >
      <InnerLayout>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12}>
              {isSuccess ? (
                <Subtitle title="All set!" />
              ) : (
                <Subtitle title="Ready to order?" />
              )}
              <Divider />
            </Grid>
            {isSuccess ? (
              <Grid container>
                <Grid item xs={0} md={2}></Grid>
                <Grid item xs={12} md={8}>
                  <CampaignSummary />
                </Grid>
              </Grid>
            ) : (
              <Grid container>
                <Grid item xs={12} sm={7} md={7} lg={7}>
                  <Stack spacing={4}>
                    <Typography
                      variant="body1"
                      gutterBottom
                      sx={styles.textMargin}
                    >
                      Almost Done! Simply provide the info associated with your
                      GroundTruth Ads Manager account.
                    </Typography>
                    <FormControlLabel
                      control={
                        <GtCheckbox
                          onChange={handleGtAccountToggle}
                          sx={styles.checkbox}
                        />
                      }
                      label="Are you a GroundTruth reseller?"
                    />
                    {store.isGtReseller ? (
                      <Grid sx={styles.inputBox} container>
                        <Grid item sx={styles.inputField}>
                          <InputTextBox
                            label="Tenant name"
                            name="tenant"
                            inputValue={store.tenantName}
                            onChange={handleTenant}
                          />
                        </Grid>

                        <Grid item sx={styles.textField}>
                          <InputTextBox
                            label="Account name"
                            name="account"
                            inputValue={store.accountName}
                            onChange={handleAccount}
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container>
                        <Grid item sx={styles.inputAccountField}>
                          <InputTextBox
                            label="Account name"
                            name="account"
                            inputValue={store.accountName}
                            onChange={handleAccount}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <InputTextBox
                      label="Your name"
                      name="name"
                      onChange={handleName}
                    />
                    <InputTextBox
                      isRequired={true}
                      label="Email address"
                      name="email"
                      onChange={handleEmail}
                    />
                    <HelpCard
                      rate={ABDM_POSTCARD_DESIGN_RATE}
                      onToggle={handleHelpToggle}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={0} sm={0.5} md={1} lg={1}></Grid>
                <Grid
                  item
                  xs={12}
                  sm={4.5}
                  md={4}
                  lg={4}
                  sx={styles.campaignDetailsBox}
                >
                  <CampaignDetailsCard />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={styles.divider} />
          {!isSuccess && (
            <Typography sx={textStyles}>
              I agree with GroundTruth{' '}
              <Link
                target="_blank"
                href="https://www.groundtruth.com/privacy-rights/"
                sx={termslinkStyle}
              >
                terms and conditions.
              </Link>
            </Typography>
          )}
          {isSuccess ? (
            <Button
              disableElevation
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ChevronRightIcon />}
              onClick={() => scrollToSection('help-section')}
            >
              Need help?
            </Button>
          ) : (
            <Button
              disableElevation
              variant="contained"
              color={isDisabled ? 'secondary' : 'primary'}
              size="large"
              disabled={isDisabled}
              endIcon={<ChevronRightIcon />}
              onClick={isDisabled ? null : onSubmit}
              sx={styles.button}
              data-testid="reserveCampaignButton"
            >
              Reserve your campaign
            </Button>
          )}
          {isError && <ApiErrorModal setIsError={setIsError} />}
        </Grid>
      </InnerLayout>
    </Grid>
  );
};

export default observer(OrderSection);
